import { fromJS, List, Map, Record } from '@gonfalon/immutable-shim/immutableShim';

import type { Links } from '#utils/linkUtils';

import { applyCommitUrlTemplate } from '../applyCommitUrlTemplate';
import { createRepoUrl } from '../createRepoUrl';

import { createReference, type Reference } from './Reference';

type BranchType = {
  _links: Links;
  name: string;
  head: string;
  syncTime: number;
  references: List<Reference>;
  sourceLink: string;
};

export class Branch extends Record<BranchType>({
  _links: Map(),
  name: '',
  head: '',
  syncTime: 0,
  references: List<Reference>(),
  sourceLink: '',
}) {
  hasReferences() {
    return this.references.size > 0;
  }

  hasContextLines() {
    // we expect every reference to have context lines if they're enabled,
    // but we use the `some` method to handle the (potential bug) edge case
    // where only some references don't have context lines
    return !this.references.some((ref) => ref.hunks && ref.hunks.size > 0 && !ref.hunks.find((hunk) => !!hunk.lines));
  }

  maybeApplyPagination(isPaginationEnabled: boolean, numItems: number) {
    return isPaginationEnabled ? this.update('references', (refs) => refs.take(numItems)) : this;
  }

  applyCommitUrlTemplate(commitUrlTemplate: string) {
    return this.set(
      'sourceLink',
      commitUrlTemplate ? applyCommitUrlTemplate(commitUrlTemplate, this.head, this.name) : '',
    );
  }

  generateCollapsedState(collapsed: boolean) {
    return this.references.map(() => collapsed);
  }

  applyHunkUrlTemplate(hunkUrlTemplate: string) {
    const newThis = this.update('references', (refs) =>
      refs.map((ref) =>
        ref.update('hunks', (hunks) =>
          hunks.map((hunk) =>
            hunk.set(
              'sourceLink',
              hunkUrlTemplate
                ? createRepoUrl(hunkUrlTemplate, {
                    sha: this.head,
                    filePath: ref.path,
                    lineNumber: hunk.linkLineNumber(),
                  })
                : '',
            ),
          ),
        ),
      ),
    );
    return newThis;
  }

  filterExtensions(extensions: List<string>) {
    if (!extensions.size) {
      return this;
    }
    return this.update('references', (refs) =>
      refs.filter((r) =>
        extensions.some((ext) => (ext === 'none' ? r.path.split('.').length === 1 : r.path.endsWith(ext))),
      ),
    );
  }
}

export function createBranch(props = {}) {
  const branch = props instanceof Branch ? props : new Branch(fromJS(props));
  return branch.update('references', (rs) => rs && rs.map(createReference));
}
